import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

const AUTO_ADVANCE_MS = 5000;

function SlideCta({ slide }) {
    if (!slide.cta_label) return null;
    const isExternal = /^https?:\/\//i.test(slide.cta_link || "");
    const isRoute = (slide.cta_link || "").startsWith("/") && !slide.cta_link?.startsWith("//");
    const className = "rounded-full bg-brand-500 hover:bg-brand-600 text-white px-7 py-6 text-base shadow-cta group";
    const inner = (
        <>
            {slide.cta_label}
            <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
        </>
    );
    if (isRoute) {
        return (
            <Button asChild className={className} data-testid={`hero-slide-cta-${slide.id}`}>
                <Link to={slide.cta_link}>{inner}</Link>
            </Button>
        );
    }
    return (
        <Button
            asChild
            className={className}
            data-testid={`hero-slide-cta-${slide.id}`}
        >
            <a href={slide.cta_link || "#"} target={isExternal ? "_blank" : undefined} rel={isExternal ? "noreferrer" : undefined}>
                {inner}
            </a>
        </Button>
    );
}

export default function HeroSlider() {
    const [slides, setSlides] = useState([]);
    const [active, setActive] = useState(0);
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        api.get("/hero-slides").then((r) => setSlides(r.data || [])).catch(() => setSlides([]));
    }, []);

    const next = useCallback(() => {
        setActive((i) => (slides.length > 0 ? (i + 1) % slides.length : 0));
    }, [slides.length]);

    const prev = useCallback(() => {
        setActive((i) => (slides.length > 0 ? (i - 1 + slides.length) % slides.length : 0));
    }, [slides.length]);

    useEffect(() => {
        if (paused || slides.length < 2) return;
        const t = setInterval(next, AUTO_ADVANCE_MS);
        return () => clearInterval(t);
    }, [paused, slides.length, next]);

    if (slides.length === 0) {
        // Skeleton — keeps layout height stable while slides load.
        return (
            <section id="home" className="relative overflow-hidden bg-paper" data-testid="hero-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
                    <div className="h-[420px] md:h-[520px] rounded-3xl bg-gray-100 animate-pulse" />
                </div>
            </section>
        );
    }

    const slide = slides[active];

    return (
        <section
            id="home"
            className="relative overflow-hidden bg-paper"
            data-testid="hero-section"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div className="blob bg-brand-200 w-[420px] h-[420px] -top-32 -left-24" />
            <div className="blob bg-accent-100 w-[380px] h-[380px] top-32 -right-24" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
                <div className="relative rounded-3xl overflow-hidden shadow-soft border border-gray-100" data-testid={`hero-slide-${slide.id}`}>
                    <div className="grid lg:grid-cols-12 min-h-[460px] md:min-h-[520px]">
                        {/* Text column */}
                        <div className="lg:col-span-6 p-8 md:p-14 flex flex-col justify-center bg-white">
                            {slide.subheading && (
                                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-50 text-brand-700 text-xs font-semibold tracking-wide uppercase w-fit" data-testid="hero-slide-subheading">
                                    <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
                                    {slide.subheading}
                                </span>
                            )}
                            <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-heading font-semibold leading-[1.1] tracking-tight text-ink-900" data-testid="hero-slide-heading">
                                {slide.heading}
                            </h1>
                            {slide.brief && (
                                <p className="mt-6 text-base md:text-lg text-ink-700 max-w-xl leading-relaxed" data-testid="hero-slide-brief">
                                    {slide.brief}
                                </p>
                            )}
                            <div className="mt-8 flex flex-wrap gap-3 items-center">
                                <SlideCta slide={slide} />
                                {slides.length > 1 && (
                                    <div className="flex items-center gap-1.5 ml-2" role="tablist" aria-label="Slide dots">
                                        {slides.map((s, i) => (
                                            <button
                                                key={s.id}
                                                onClick={() => setActive(i)}
                                                className={`h-2 rounded-full transition-all ${i === active ? "w-8 bg-brand-500" : "w-2 bg-gray-300 hover:bg-gray-400"}`}
                                                aria-label={`Go to slide ${i + 1}`}
                                                aria-selected={i === active}
                                                role="tab"
                                                data-testid={`hero-dot-${i}`}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Image column */}
                        <div className="lg:col-span-6 relative bg-gray-100 min-h-[280px]">
                            <img
                                src={slide.image_url}
                                alt={slide.heading}
                                className="absolute inset-0 w-full h-full object-cover"
                                key={slide.id}
                            />
                            <div className="absolute inset-0 bg-gradient-to-tr from-black/15 via-transparent to-transparent lg:hidden" />
                        </div>
                    </div>

                    {/* Arrow controls */}
                    {slides.length > 1 && (
                        <>
                            <button
                                onClick={prev}
                                className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 md:h-12 md:w-12 rounded-full bg-white/95 backdrop-blur text-ink-900 hover:bg-brand-500 hover:text-white shadow-soft flex items-center justify-center transition"
                                aria-label="Previous slide"
                                data-testid="hero-prev-btn"
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </button>
                            <button
                                onClick={next}
                                className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 md:h-12 md:w-12 rounded-full bg-white/95 backdrop-blur text-ink-900 hover:bg-brand-500 hover:text-white shadow-soft flex items-center justify-center transition"
                                aria-label="Next slide"
                                data-testid="hero-next-btn"
                            >
                                <ChevronRight className="h-5 w-5" />
                            </button>
                        </>
                    )}
                </div>
            </div>
        </section>
    );
}
